// Iter 107 — One-click contact chips on the composer.
// When a client is tagged on the floating composer (via tagClientId),
// surface that client's saved contacts as chips users can tap to add to
// the To / Cc / Bcc field — no need to remember email addresses for
// directors, bookkeepers, VAT signatories, etc.
//
// • Chip text   : contact full name
// • Sub-hint    : position (or "Primary" if no position)
// • Click       : append email to the active field (To by default).
//                 Hold Cmd/Ctrl-click → add to Cc instead.
// • Active state: chip is filled when its email is already present in
//                 ANY of To/Cc/Bcc.
// • Hidden when : no client tagged OR client has no saved contacts.

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { UserPlus, Star } from "lucide-react";

// Robust enough — emails inside `to` strings are typically comma- or
// semicolon-separated, sometimes wrapped in "Name <email>" form.
const extractEmails = (str) => {
  if (!str) return [];
  return Array.from(str.matchAll(/[\w.+-]+@[\w-]+\.[\w.-]+/g)).map((m) => m[0].toLowerCase());
};

const appendEmail = (current, contact) => {
  const formatted = contact.name && contact.email
    ? `${contact.name} <${contact.email}>`
    : (contact.email || "");
  if (!formatted) return current || "";
  const trimmed = (current || "").trim();
  if (!trimmed) return formatted;
  // If the user already typed a trailing comma, just append.
  return trimmed.endsWith(",") ? `${trimmed} ${formatted}` : `${trimmed}, ${formatted}`;
};

export default function ClientContactChips({
  clientId,
  to,
  cc,
  bcc,
  onAddTo,
  onAddCc,
  onShowCcBcc,
}) {
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    if (!clientId) { setContacts([]); setLoaded(true); return () => {}; }
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}/contacts`);
        if (!cancelled) {
          // Only chips for contacts that actually have an email — the
          // whole point is one-click "To:" insertion.
          setContacts((r.data || []).filter((c) => (c.email || "").trim()));
        }
      } catch (_e) {
        if (!cancelled) setContacts([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const presentEmails = useMemo(() => {
    return new Set([
      ...extractEmails(to),
      ...extractEmails(cc),
      ...extractEmails(bcc),
    ]);
  }, [to, cc, bcc]);

  if (!clientId || !loaded || contacts.length === 0) return null;

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap px-4 pb-2 pt-1 border-b border-slate-100"
      data-testid="client-contact-chips"
    >
      <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1 inline-flex items-center gap-1">
        <UserPlus className="h-3 w-3" /> Client contacts
      </span>
      {contacts.map((c) => {
        const isAdded = presentEmails.has((c.email || "").toLowerCase());
        const role = c.position || (c.is_primary ? "Primary" : "");
        return (
          <button
            key={c.contact_id}
            type="button"
            data-testid={`contact-chip-${c.contact_id}`}
            data-added={isAdded ? "true" : "false"}
            onClick={(e) => {
              // Iter 107a — re-click of an already-added chip is a no-op.
              // The dark state implies "this email is in the recipients";
              // appending again would contradict that promise and create
              // duplicate pills in To/Cc/Bcc.
              if (isAdded) return;
              // Cmd/Ctrl-click → Cc. Plain click → To.
              if (e.metaKey || e.ctrlKey) {
                onShowCcBcc?.();
                onAddCc?.(c);
              } else {
                onAddTo?.(c);
              }
            }}
            aria-pressed={isAdded}
            className={[
              "group inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors",
              isAdded
                ? "bg-slate-900 border-slate-900 text-white hover:bg-slate-800"
                : "bg-white border-slate-300 text-slate-700 hover:border-slate-900 hover:text-slate-900",
            ].join(" ")}
            title={
              isAdded
                ? `${c.name} <${c.email}> — already added`
                : `Add ${c.email} to To · ${navigator?.platform?.startsWith("Mac") ? "⌘" : "Ctrl"}-click for Cc`
            }
          >
            {c.is_primary && (
              <Star
                className={`h-2.5 w-2.5 ${
                  isAdded ? "fill-amber-300 text-amber-300" : "fill-amber-500 text-amber-500"
                }`}
              />
            )}
            <span className="font-medium">{c.name}</span>
            {role && (
              <span
                className={`text-[10px] ${isAdded ? "text-slate-300" : "text-slate-400 group-hover:text-slate-500"}`}
              >
                · {role}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Re-export the helper so the composer can reuse the same append rules.
export { appendEmail };
